import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { personDisplayName } from "@family/shared";
import type {
  BranchModeratorsResponse,
  BranchesResponse,
  FamilyMembersResponse,
  FamilySettings,
  PersonsListResponse,
  UpdateBranchModeratorsRequest,
} from "@family/shared";

/**
 * Settings → Branch moderators. One card per branch (child of the founder)
 * with a checkbox per admin/moderator. A sign-up that names a branch is
 * emailed and pushed to the people ticked for it.
 */
export function BranchModeratorsAdmin({ familySlug }: { familySlug: string }) {
  const queryClient = useQueryClient();
  const branches = useQuery<BranchesResponse>({
    queryKey: ["branches", familySlug],
    queryFn: () => apiFetch<BranchesResponse>(`/families/${familySlug}/branches`),
  });
  const members = useQuery<FamilyMembersResponse>({
    queryKey: ["members", familySlug],
    queryFn: () => apiFetch<FamilyMembersResponse>(`/families/${familySlug}/members`),
  });
  const assignments = useQuery<BranchModeratorsResponse>({
    queryKey: ["branch-moderators", familySlug],
    queryFn: () =>
      apiFetch<BranchModeratorsResponse>(`/families/${familySlug}/branch-moderators`),
  });
  const save = useMutation<BranchModeratorsResponse, Error, UpdateBranchModeratorsRequest>({
    mutationFn: (body) =>
      apiFetch<BranchModeratorsResponse>(`/families/${familySlug}/branch-moderators`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["branch-moderators", familySlug], data);
    },
  });

  // Which person's children count as branches. Null = auto-detected root.
  const [rootSearch, setRootSearch] = useState("");
  const rootResults = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug, rootSearch],
    queryFn: () =>
      apiFetch<PersonsListResponse>(
        `/families/${familySlug}/persons?q=${encodeURIComponent(rootSearch)}`,
      ),
    enabled: rootSearch.trim().length >= 2,
  });
  const setRoot = useMutation<FamilySettings, Error, string | null>({
    mutationFn: (branchRootPersonId) =>
      apiFetch<FamilySettings>(`/families/${familySlug}/settings`, {
        method: "PATCH",
        body: JSON.stringify({ branchRootPersonId }),
      }),
    onSuccess: () => {
      setRootSearch("");
      void queryClient.invalidateQueries({ queryKey: ["branches", familySlug] });
      void queryClient.invalidateQueries({ queryKey: ["settings", familySlug] });
    },
  });

  if (branches.isLoading || members.isLoading || assignments.isLoading) {
    return <p className="text-muted">Loading…</p>;
  }
  const err = branches.error ?? members.error ?? assignments.error;
  if (err) return <p className="text-error">{(err as Error).message}</p>;

  // rootPersonId is filled in whether the root was saved or guessed, so it
  // can't drive this copy: the guess lands on a plausible-looking name and
  // the panel then reads as configured when nothing is stored. Only
  // rootIsExplicit separates the two.
  const rootIsExplicit = branches.data?.rootIsExplicit ?? false;
  const rootPicker = (
    <div className="card stack" style={{ padding: "12px 16px", gap: 6 }}>
      <div style={{ fontSize: "0.9rem" }}>
        Branches are the children of{" "}
        <strong>{branches.data?.founderName ?? "— (no root found)"}</strong>
        {rootIsExplicit && (
          <>
            <span className="badge" style={{ marginLeft: 8 }}>
              Saved
            </span>
            <button
              type="button"
              className="btn-tertiary"
              style={{ marginLeft: 8, padding: "2px 8px", fontSize: "0.8rem", minHeight: 0 }}
              onClick={() => setRoot.mutate(null)}
              disabled={setRoot.isPending}
            >
              Clear and auto-detect
            </button>
          </>
        )}
      </div>
      {!rootIsExplicit && branches.data?.rootPersonId && (
        <p className="text-muted" style={{ margin: 0, fontSize: "0.85rem" }}>
          <strong>Auto-detected, not saved.</strong> Nothing is stored for this
          family, so this is a guess — the person with no recorded parents and
          the most descendants. It changes as the tree is edited, and on a tree
          that reaches above the founder it picks the wrong person. Search below
          and pick the founder to fix it, even when the name above already looks
          right.
        </p>
      )}
      <div style={{ display: "flex", gap: "var(--s-2)", alignItems: "center", flexWrap: "wrap", fontSize: "0.85rem" }}>
        <span className="text-muted">Change:</span>
        <input
          type="text"
          value={rootSearch}
          onChange={(e) => setRootSearch(e.target.value)}
          placeholder="Search the founder in the tree…"
          style={{ minHeight: 32, fontSize: "0.85rem", padding: "4px 10px", width: "auto", flex: "0 1 260px" }}
        />
      </div>
      {(rootResults.data?.persons ?? []).length > 0 && (
        <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
          {rootResults.data!.persons.slice(0, 6).map((p) => (
            <button
              key={p.id}
              type="button"
              className="tree-chip"
              style={{ background: "transparent", color: "var(--c-text)", border: "1px solid var(--c-border-strong)" }}
              onClick={() => setRoot.mutate(p.id)}
              disabled={setRoot.isPending}
            >
              {personDisplayName(p)}
              {p.birthDate ? ` (${p.birthDate.slice(0, 4)})` : ""}
            </button>
          ))}
        </div>
      )}
      {setRoot.error && <p className="text-error">{setRoot.error.message}</p>}
    </div>
  );

  const list = branches.data?.branches ?? [];
  const mods = (members.data?.members ?? []).filter(
    (m) => m.role === "admin" || m.role === "moderator",
  );
  if (list.length === 0) {
    return (
      <div className="stack" style={{ gap: "var(--s-2)" }}>
        {rootPicker}
        <p className="text-muted">
          No branches yet — the tree needs a founder with recorded children.
        </p>
      </div>
    );
  }
  const assigned = new Map<string, Set<string>>();
  for (const a of assignments.data?.assignments ?? []) {
    const set = assigned.get(a.branchPersonId) ?? new Set<string>();
    set.add(a.userId);
    assigned.set(a.branchPersonId, set);
  }

  return (
    <div className="stack" style={{ gap: "var(--s-2)" }}>
      {rootPicker}
      {list.map((b) => {
        const current = assigned.get(b.id) ?? new Set<string>();
        return (
          <div key={b.id} className="card" style={{ padding: "12px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <strong>
                {b.name}
                {b.birthYear && (
                  <span className="text-subtle" style={{ fontWeight: 400 }}> · b. {b.birthYear}</span>
                )}
              </strong>
              {current.size === 0 && (
                <span className="text-subtle" style={{ fontSize: "0.8rem" }}>nobody assigned</span>
              )}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 8 }}>
              {mods.map((m) => (
                <label
                  key={m.userId}
                  style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 400, fontSize: "0.9rem", margin: 0 }}
                >
                  <input
                    type="checkbox"
                    checked={current.has(m.userId)}
                    disabled={save.isPending}
                    onChange={(e) => {
                      const next = new Set(current);
                      if (e.target.checked) next.add(m.userId);
                      else next.delete(m.userId);
                      save.mutate({ branchPersonId: b.id, userIds: [...next] });
                    }}
                  />
                  {m.displayName ?? m.email ?? m.phone ?? m.userId.slice(0, 8)}
                  <span className="text-subtle" style={{ fontSize: "0.75rem" }}>{m.role}</span>
                </label>
              ))}
              {mods.length === 0 && (
                <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                  No admins or moderators to assign.
                </span>
              )}
            </div>
          </div>
        );
      })}
      {save.error && <p className="text-error">{save.error.message}</p>}
    </div>
  );
}
